import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Common";
import { IMAGES, VALUES, ASSETS, FOUNDER } from "../mock/mock";

const MODEL = [
  "Aggregate farmers to facilitate collective farming of agarwood",
  "Generate employment by skilling the rural workforce",
  "Empower women and youth through skilling and outreach",
  "Promote ethical business practices across the industry",
  "Act as an interface between farmers and government",
  "Support the national Agarwood Promotion Policy, 2020",
  "Establish international trade channels for agarwood",
  "Contribute to the carbon credit industry",
];

const About = () => {
  return (
    <div className="bg-noir">
      <Seo
        title="About — Sustainable Agarwood Plantation & Oud House | Meilleur"
        description="Meilleur is a farmer-first agarwood plantation, inoculation and aromatics house across Assam, Tripura and Nagaland — 103 acres, 27 distillation units, and a mission for sustainable oud."
        path="/about"
        keywords="agarwood plantation, sustainable oud, agarwood company India, oud plantation Assam, agarwood inoculation, Meilleur Plantations"
      />
      <PageHero eyebrow="Company Overview" title="About the" accent="House" image={IMAGES.forest} crumbs={["About"]} />

      {/* Story */}
      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <Reveal><SectionHeading eyebrow="Who We Are" title="A farmer-first" accent="agarwood house" /></Reveal>
            <Reveal delay={120}>
              <div className="mt-8 space-y-6 text-[var(--ivory-muted)] text-lg leading-relaxed font-body font-light">
                <p>
                  Meilleur Plantations is an agarwood plantation, inoculation and aromatics company headquartered in New
                  Delhi, with working plantations across Assam, Tripura and Nagaland. Registered with MSME, GST, DGFT,
                  FSSAI and APEDA, it was built to turn one of India's most valuable — and most overlooked — natural
                  resources into a transparent, farmer-first industry.
                </p>
                <p>
                  Within six years, the company has grown into a vertically integrated operation — from sapling to
                  plantation, distillation to finished fragrance — anchoring four distinct brands under one house, led
                  by our flagship <span className="text-[var(--gold-light)]">Meilleur Oudh &amp; Aromas</span>.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={160}>
              <div className="img-zoom relative">
                <img src={IMAGES.forest2} alt="Aquilaria plantation" className="w-full h-[480px] object-cover" />
                <div className="absolute inset-0 border border-[var(--line)]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission Vision */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8">
          {[
            { t: "Mission", d: "To provide farmers with a sustainable opportunity to grow agarwood, while promoting responsible forestry practices and contributing to biodiversity." },
            { t: "Vision", d: "To be the global leader in sustainable agarwood plantation and cultivation — empowering farmers and communities while preserving the natural environment." },
          ].map((m, i) => (
            <Reveal key={m.t} delay={i * 120}>
              <div className="lux-card p-10 md:p-14 h-full">
                <div className="eyebrow mb-5">{m.t}</div>
                <p className="font-serif-display text-2xl md:text-[1.8rem] leading-[1.35]">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="What Guides Us" title="Our" accent="values" align="center" /></Reveal>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-7">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 100}>
                <div className="border-t border-[var(--gold)] pt-6 h-full">
                  <div className="font-serif-display text-5xl gold-text opacity-30 mb-4">0{i + 1}</div>
                  <h3 className="font-serif-display text-2xl mb-3">{v.title}</h3>
                  <p className="text-sm text-[var(--ivory-muted)] leading-relaxed font-body">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Samaj Bazar Sarkar */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal><SectionHeading eyebrow="Our Operating Model" title="Samaj · Bazar ·" accent="Sarkar" /></Reveal>
            <Reveal delay={120}>
              <p className="mt-6 text-[var(--ivory-muted)] font-body font-light leading-relaxed max-w-lg">
                Society, Market, Government — our operations are deliberately built to serve and connect all three.
              </p>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {MODEL.map((m, i) => (
              <Reveal key={m} delay={i * 60}>
                <div className="flex gap-4 py-3 border-b border-[var(--line)]">
                  <span className="text-[var(--gold)] text-xs mt-1 font-body">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm text-[var(--ivory-muted)] font-body leading-relaxed">{m}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Assets */}
      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="Within 6 Years" title="Assets at a" accent="glance" align="center" /></Reveal>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)]">
            {ASSETS.map((a, i) => (
              <Reveal key={a.label} delay={i * 90}>
                <div className="bg-noir p-8 md:p-12 text-center h-full">
                  <div className="font-serif-display text-4xl md:text-5xl gold-text">{a.value}</div>
                  <div className="text-sm tracking-[0.14em] uppercase mt-3 text-[var(--ivory)]">{a.label}</div>
                  <div className="text-xs text-[var(--ivory-muted)] mt-1 font-body">{a.sub}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="img-zoom relative">
                <img src={IMAGES.forest2} alt="Leadership" className="w-full h-[500px] object-cover" />
                <div className="absolute inset-0 border border-[var(--line)]" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><div className="eyebrow mb-5">Leadership</div></Reveal>
            <Reveal delay={100}>
              <h2 className="font-serif-display text-4xl md:text-5xl">{FOUNDER.name}</h2>
              <p className="text-[var(--gold-light)] mt-2 tracking-[0.14em] uppercase text-xs font-body">{FOUNDER.role}</p>
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-7 text-[var(--ivory-muted)] leading-relaxed font-body font-light">{FOUNDER.bio}</p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-9 grid grid-cols-3 gap-6">
                {FOUNDER.stats.map((s) => (
                  <div key={s.label} className="border-l border-[var(--line)] pl-4">
                    <div className="font-serif-display text-3xl gold-text">{s.value}</div>
                    <div className="text-[0.68rem] tracking-[0.12em] uppercase text-[var(--ivory-muted)] mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 text-center">
        <Reveal>
          <p className="eyebrow mb-6">Discover the Craft</p>
          <h2 className="font-serif-display text-4xl md:text-5xl mb-8">Explore our <span className="gold-text italic">collections</span></h2>
          <Link to="/oud-chips" className="btn-gold">View Oud Chips <ArrowRight size={15} /></Link>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
